import { Desk, DeskBookingStatus, loadDesks, useAppDispatch, withAsyncThunkErrorHandling } from "@lib/store";
import { MapSelectorCard_internal } from "./MapSelectorCard_internal";
import { useEffect, useState } from "react";
import { isDeskReservation } from "@lib/store/src/data/timeslots/apiTypes";
import { MapViewUser } from "@lib/utils";
import { SelectedDeskUser } from "../DeskSelectionModalPage/types";
import { ToastData } from "./types";
import { useTranslation } from "react-i18next";
import { Attendee } from "@lib/store/src/data/timeslots/selectors/getAttendeeOverview";
import { useUserProfilePics } from "@hooks";

type MapSelectorProps = {
  attendees: Array<Attendee>;
};

// TODO: should cache this in the store probably... this only supports switching area without unmounting
const useDesks = (areaId: string) => {
  const dispatch = useAppDispatch();

  const [desksByAreaId, setDesksByAreaId] = useState<Record<string, Array<Desk> | null | undefined>>({});

  useEffect(
    function loadDesksAndAddToCache() {
      if (areaId in desksByAreaId) return;

      // Null indicates load
      setDesksByAreaId(prev => ({ ...prev, [areaId]: null }));

      dispatch(withAsyncThunkErrorHandling(() => loadDesks(areaId))).then(result => {
        if (result.success) {
          setDesksByAreaId(prev => ({ ...prev, [areaId]: result.result }));
        }
      });
    },
    [areaId, desksByAreaId, dispatch],
  );

  return desksByAreaId[areaId];
};

export const MapSelectorCard = ({ attendees }: MapSelectorProps) => {
  const { t } = useTranslation();

  const buildings = uniques(attendees.map(m => m.reservation.workspace.buildingId));
  const floors = uniques(
    attendees.map(m => (m.reservation.workspace.nodeType !== "Building" ? m.reservation.workspace.floorId : null)),
  );
  const areas = uniques(
    attendees.map(m =>
      m.reservation.workspace.nodeType === "Desk" || m.reservation.workspace.nodeType === "Area"
        ? m.reservation.workspace.areaId
        : null,
    ),
  );

  if (buildings.length !== 1 || floors.length !== 1 || areas.length !== 1) {
    throw new Error("FullMapView requires members to all be in the same area");
  }

  const buildingId = buildings[0];
  const floorId = floors[0];
  const areaId = areas[0];

  if (!areaId || !floorId || !buildingId) {
    throw new Error("FullMapView requires members to be at least in an area");
  }

  const desks = useDesks(areaId);
  const deskIds = Array<string>();
  const connectionsByDeskId = new Map<string, MapViewUser>();
  const deskIdsBookedByOthers: DeskBookingStatus = {};
  const toasterData: Record<string, ToastData> = {};

  for (const { reservation, owner, isCurrentUser } of attendees) {
    if (!isDeskReservation(reservation)) continue;

    const deskId = reservation.workspace.deskId;
    deskIds.push(deskId);
    connectionsByDeskId.set(deskId, {
      color: owner.email,
      initials: owner.initials,
      picture: owner.imageHash ?? "",
    });

    toasterData[reservation.workspace.deskId] = {
      displayName: isCurrentUser ? t("You") : owner.name,
      user: owner,
      deskName: reservation.workspace.deskName,
    };
  }

  for (const deskId of deskIds) {
    if (!connectionsByDeskId.has(deskId)) {
      // TODO: fetch desk availability instead of assuming true
      deskIdsBookedByOthers[deskId] = true;
    }
  }

  const connectionProfileImages = useUserProfilePics(
    attendees.map(a => ({
      userId: a.owner.id,
      imageHash: a.owner.imageHash ?? "",
    })),
  );

  const users = attendees.map((m): SelectedDeskUser => {
    return {
      email: m.owner.email,
      id: m.reservation.owner.id,
      initials: m.owner.initials,
      imageHash: m.owner.imageHash ?? "",
      imageUrl: connectionProfileImages[m.owner.id],
    };
  });

  return (
    <MapSelectorCard_internal
      buildingId={buildingId}
      floorId={floorId}
      areaId={areaId}
      deskIds={deskIds.length ? deskIds : undefined}
      connectionsByDeskId={connectionsByDeskId}
      deskIdsBookedByOthers={deskIdsBookedByOthers}
      desks={desks ?? []}
      loading={!desks}
      users={users}
      toasterData={toasterData}
    />
  );
};

function uniques<T>(xs: Array<T>): Array<T> {
  return Array.from(new Set(xs));
}
